import type { StoredResource } from "../schema/common.js";

export const WORLD_SIZE = 300;
export const VAULT_PACK_LEVEL = 38;
export const WORLD_CENTRE = 150;
export const CHUNK_SIZE = 10;

export type RingTile = "ash" | "rust" | "frost" | "glass" | "necro" | "ground_zero";

export interface RingDef {
  id: 1 | 2 | 3 | 4 | 5 | 6;
  key: "ashlands" | "rustplains" | "frostbelt" | "glasswaste" | "necropolis" | "ground_zero";
  name: { de: string; en: string };
  tile: RingTile;
  color: string;
  /** Euclidean distance from the centre (inclusive min, exclusive max; ring 1 also covers the corners). */
  minDist: number;
  maxDist: number;
  packLevels: [number, number];
  nodeLevels: [number, number];
  hiveLevels: [number, number] | null;
  /** Occupants per 100 tiles. */
  density: { packs: number; nodes: number; elites: number; hives: number; ruins: number; camps: number; vaults: number };
}

/** Six threat rings toward Ground Zero (50-world.md R40). */
export const RINGS: readonly RingDef[] = [
  { id: 1, key: "ashlands", name: { de: "Ascheland", en: "Ashlands" }, tile: "ash", color: "#6b7784", minDist: 120, maxDist: 1000, packLevels: [1, 8], nodeLevels: [1, 2], hiveLevels: null, density: { packs: 6, nodes: 5, elites: 0, hives: 0, ruins: 0.6, camps: 0.5, vaults: 0 } },
  { id: 2, key: "rustplains", name: { de: "Rostebenen", en: "Rustplains" }, tile: "rust", color: "#b45309", minDist: 95, maxDist: 120, packLevels: [8, 15], nodeLevels: [2, 3], hiveLevels: null, density: { packs: 7, nodes: 5, elites: 0.6, hives: 0, ruins: 0.8, camps: 0.4, vaults: 0 } },
  { id: 3, key: "frostbelt", name: { de: "Frostgürtel", en: "Frostbelt" }, tile: "frost", color: "#38bdf8", minDist: 70, maxDist: 95, packLevels: [15, 22], nodeLevels: [3, 4], hiveLevels: [1, 4], density: { packs: 7, nodes: 4.5, elites: 0.8, hives: 0.5, ruins: 0.8, camps: 0.3, vaults: 0 } },
  { id: 4, key: "glasswaste", name: { de: "Glasödnis", en: "Glasswaste" }, tile: "glass", color: "#a78bfa", minDist: 45, maxDist: 70, packLevels: [22, 30], nodeLevels: [5, 6], hiveLevels: [5, 7], density: { packs: 8, nodes: 4, elites: 1, hives: 0.6, ruins: 0.8, camps: 0.2, vaults: 0 } },
  { id: 5, key: "necropolis", name: { de: "Nekropole", en: "Necropolis" }, tile: "necro", color: "#22c55e", minDist: 20, maxDist: 45, packLevels: [30, 36], nodeLevels: [7, 7], hiveLevels: [8, 10], density: { packs: 9, nodes: 3.5, elites: 1.2, hives: 0.8, ruins: 0.6, camps: 0.1, vaults: 0.4 } },
  { id: 6, key: "ground_zero", name: { de: "Ground Zero", en: "Ground Zero" }, tile: "ground_zero", color: "#ef4444", minDist: 0, maxDist: 20, packLevels: [36, 40], nodeLevels: [8, 8], hiveLevels: null, density: { packs: 10, nodes: 3, elites: 1.5, hives: 0, ruins: 0.4, camps: 0, vaults: 0 } },
];

export function ringById(id: number): RingDef {
  const r = RINGS.find((x) => x.id === id);
  if (!r) throw new Error(`unknown ring ${id}`);
  return r;
}

export type NodeKind = "depot" | "dump" | "mine" | "well";
export const NODE_KINDS: readonly NodeKind[] = ["depot", "dump", "mine", "well"];
export const NODE_RESOURCE: Record<NodeKind, StoredResource> = { depot: "RAT", dump: "POL", mine: "ALY", well: "ENC" };
export const NODE_NAMES: Record<NodeKind, { de: string; en: string }> = {
  depot: { de: "Rationsdepot", en: "Ration Depot" },
  dump: { de: "Polymerhalde", en: "Polymer Dump" },
  mine: { de: "Legierungsmine", en: "Alloy Mine" },
  well: { de: "Energiequelle", en: "Energy Well" },
};
/** Deposit per node level 1..8. */
export const NODE_DEPOSITS: readonly number[] = [20_000, 45_000, 90_000, 180_000, 320_000, 540_000, 800_000, 1_200_000];
export const GATHER_BASE_PER_HOUR = 6_000;
export const GATHER_GROWTH = 1.35;

export const MARCH_TILES_PER_MIN = 10;
export const MARCH_MAX_MINUTES = 60;
export const SCOUT_MINUTES = 2;

export type MarchAction = "gather" | "attack" | "scout" | "reinforce";
export type TileKind = "empty" | "enclave" | "node" | "pack" | "elite" | "hive" | "boss" | "ruin" | "camp" | "vault" | "citadel";

/** Stamina per action by target kind (R42). */
export const STAMINA_COST: Record<string, number> = { gather: 0, scout: 0, reinforce: 0, "attack:pack": 10, "attack:elite": 20, "attack:hive": 25, "attack:boss": 30, "attack:enclave": 10, "attack:vault": 10 };

export const PVP = {
  lootPct: 20,
  hqDiffMax: 6,
  burnMinutes: 30,
  burnProductionPct: 50,
  marksWin: 10,
  marksDefend: 3,
  revengePct: 10,
  revengeHours: 24,
  wallRepairMinutes: 60,
  wallDamagePct: 40,
  extinguishCredits: 20,
} as const;

export const SHIELDS = {
  newbieHours: 72,
  /** Hours → Credits (60-store.md), matching the shield items. */
  credits: { 8: 60, 24: 150, 72: 400, 168: 800 } as Record<number, number>,
  items: { 8: "itm.shield_8h", 24: "itm.shield_24h", 72: "itm.shield_3d", 168: "itm.shield_7d" } as Record<number, string>,
} as const;

export const TELEPORTS = { random: { credits: 200, item: "itm.teleport_random" }, advanced: { credits: 600, item: "itm.teleport_advanced" } } as const;

export const WORLD_BOSS = {
  intervalHours: 48,
  windowMinutes: 30,
  ring: 4,
  /** Rewards by damage rank; everyone who dealt damage gets the last row. */
  rewards: [
    { fromRank: 1, toRank: 1, items: [{ itemId: "itm.cdc", qty: 120 }, { itemId: "itm.ufr_s", qty: 30 }, { itemId: "itm.ffab", qty: 3 }], credits: 100 },
    { fromRank: 2, toRank: 5, items: [{ itemId: "itm.cdc", qty: 80 }, { itemId: "itm.ufr_a", qty: 30 }, { itemId: "itm.fab", qty: 5 }], credits: 50 },
    { fromRank: 6, toRank: 20, items: [{ itemId: "itm.cdc", qty: 40 }, { itemId: "itm.ufr_b", qty: 20 }, { itemId: "itm.fab", qty: 2 }], credits: 20 },
    { fromRank: 21, toRank: 1_000_000, items: [{ itemId: "itm.cdc", qty: 15 }, { itemId: "itm.token_salvage", qty: 50 }], credits: 5 },
  ],
} as const;

/** Pack fight rewards: Scrip, salvage tokens and a CDC chance scale with the pack level (R45). */
export function packRewards(level: number): { resources: Partial<Record<"SCR", number>>; items: { itemId: string; qty: number }[]; cdcChancePct: number } {
  return { resources: { SCR: 20 + level * 6 }, items: [{ itemId: "itm.token_salvage", qty: 2 + Math.floor(level / 4) }], cdcChancePct: Math.min(60, 10 + level * 1.2) };
}

export function eliteRewards(level: number): { resources: Partial<Record<"SCR", number>>; items: { itemId: string; qty: number }[] } {
  return { resources: { SCR: 60 + level * 10 }, items: [{ itemId: "itm.cdc", qty: 2 + Math.floor(level / 5) }, { itemId: "itm.token_salvage", qty: 6 + Math.floor(level / 3) }] };
}

/** Hive chest when the Matriarch falls (R45). */
export function hiveRewards(hiveLevel: number): { items: { itemId: string; qty: number }[]; credits: number } {
  return {
    items: [
      { itemId: "itm.cdc", qty: 10 * hiveLevel },
      { itemId: `itm.ufr_${hiveLevel >= 8 ? "s" : hiveLevel >= 5 ? "a" : "b"}`, qty: 5 + hiveLevel },
      { itemId: "itm.speedup_general_60", qty: hiveLevel },
    ],
    credits: 5 * hiveLevel,
  };
}

export interface RuinOutcome {
  id: string;
  weight: number;
  name: { de: string; en: string };
  reward: { resources?: Partial<Record<StoredResource | "SCR", number>>; items?: { itemId: string; qty: number }[]; credits?: number };
}

/** Ruins: one-time exploration with a choice, resolved by a seeded weighted roll (R46). */
export const RUIN_TABLES: Record<"force" | "hack", RuinOutcome[]> = {
  force: [
    { id: "cache", weight: 50, name: { de: "Versteck geöffnet", en: "Cache broken open" }, reward: { resources: { RAT: 8_000, POL: 8_000, SCR: 400 } } },
    { id: "armory", weight: 25, name: { de: "Alte Waffenkammer", en: "Old armoury" }, reward: { items: [{ itemId: "itm.nano", qty: 12 }, { itemId: "itm.cdc", qty: 10 }] } },
    { id: "collapse", weight: 20, name: { de: "Einsturz, nur Schrott", en: "Collapse, only scrap" }, reward: { items: [{ itemId: "itm.token_salvage", qty: 15 }] } },
    { id: "relic", weight: 5, name: { de: "Reliquie!", en: "Relic!" }, reward: { items: [{ itemId: "itm.ffab", qty: 1 }, { itemId: "itm.ufr_a", qty: 10 }], credits: 30 } },
  ],
  hack: [
    { id: "data", weight: 45, name: { de: "Datenkerne geborgen", en: "Data cores recovered" }, reward: { items: [{ itemId: "itm.cdc", qty: 18 }] } },
    { id: "blueprint", weight: 25, name: { de: "Blaupause im Speicher", en: "Blueprint in storage" }, reward: { items: [{ itemId: "itm.blueprint:mod.vlk_core_b", qty: 1 }, { itemId: "itm.nano", qty: 6 }] } },
    { id: "lockout", weight: 25, name: { de: "Ausgesperrt, Sensordaten", en: "Locked out, sensor data" }, reward: { resources: { SCR: 250 }, items: [{ itemId: "itm.token_salvage", qty: 8 }] } },
    { id: "jackpot", weight: 5, name: { de: "Jackpot", en: "Jackpot" }, reward: { items: [{ itemId: "itm.fab", qty: 5 }, { itemId: "itm.cdc", qty: 30 }], credits: 40 } },
  ],
};

export interface CampTrade {
  id: string;
  give: Partial<Record<StoredResource, number>>;
  get: { SCR: number };
}

/** Survivor camps: daily trades and three daily quests (R46). */
export const CAMP_TRADES: readonly CampTrade[] = [
  { id: "rations", give: { RAT: 5_000 }, get: { SCR: 200 } },
  { id: "polymer", give: { POL: 5_000 }, get: { SCR: 200 } },
  { id: "alloy", give: { ALY: 2_000 }, get: { SCR: 260 } },
];

export interface CampQuest {
  id: string;
  name: { de: string; en: string };
  kind: "kill_packs" | "gather" | "scout";
  target: number;
  reward: { resources?: Partial<Record<"SCR", number>>; items?: { itemId: string; qty: number }[]; credits?: number };
}

export const CAMP_QUESTS: readonly CampQuest[] = [
  { id: "q.kill3", name: { de: "Drei Rudel vernichten", en: "Destroy three packs" }, kind: "kill_packs", target: 3, reward: { resources: { SCR: 300 }, items: [{ itemId: "itm.cdc", qty: 5 }] } },
  { id: "q.gather1", name: { de: "Eine Ladung sammeln", en: "Bring home one load" }, kind: "gather", target: 1, reward: { resources: { SCR: 200 }, items: [{ itemId: "itm.speedup_general_15", qty: 1 }] } },
  { id: "q.scout1", name: { de: "Ein Ziel ausspähen", en: "Scout one target" }, kind: "scout", target: 1, reward: { resources: { SCR: 100 }, credits: 2 } },
];

export const CAMP_TRADES_PER_DAY = 3;

export type BotPersonality = "turtle" | "farmer" | "raider" | "opportunist" | "warlord" | "newbie";
export const BOT_PERSONALITIES: readonly BotPersonality[] = ["turtle", "farmer", "raider", "opportunist", "warlord", "newbie"];

export interface BotRosterEntry {
  name: string;
  personality: BotPersonality;
  /** HQ level the bot is seeded toward at world start (3–28). */
  hq: number;
  /** Portrait unit shown as the enclave avatar. */
  avatar: string;
}

const FIRST = ["Ash", "Cinder", "Vex", "Kilo", "Nyx", "Rook", "Sable", "Tarn", "Orin", "Mara", "Hollow", "Jax", "Quill", "Rime", "Sol", "Vale", "Wren", "Zed", "Brann", "Cato", "Dusk", "Echo", "Fenn", "Gale", "Halo", "Ione", "Jett", "Kaya", "Lux", "Moss"];
const LAST = ["Kessler", "Varga", "Okoro", "Lindqvist", "Marrow", "Draven", "Ishikawa", "Petrov", "Nakamura", "Steel", "Halden", "Reyes", "Volkov", "Ambrose", "Thorne", "Castellan", "Ferro", "Grimsby", "Holt", "Ivanov"];
const AVATARS = ["unit.aegis_prime", "unit.overwatch_nyx", "unit.warden_kastor", "unit.sable_marrow", "unit.ironclad_boone", "unit.halcyon_vex", "unit.sgt_rook", "unit.cipher_odd", "unit.bulwark_titan", "unit.hellion_vanguard", "unit.widow_zero", "unit.seraph_unit"];

/** 120 NPC enclaves with names, personalities and target HQ levels spread 3–28 (R47). */
export const BOT_ROSTER: readonly BotRosterEntry[] = Array.from({ length: 120 }, (_, i) => {
  const first = FIRST[(i * 7) % FIRST.length]!;
  const last = LAST[(i * 11) % LAST.length]!;
  const personality = BOT_PERSONALITIES[i % BOT_PERSONALITIES.length]!;
  const hq = 3 + Math.round((i / 119) * 25);
  return { name: `${first}_${last}${i < 10 ? "" : ""}`.replace(/[^A-Za-z0-9_]/g, "").slice(0, 20) + (i >= FIRST.length * 2 ? String(i) : ""), personality, hq, avatar: AVATARS[i % AVATARS.length]! };
});

/** Per-personality rules table (R47). */
export interface PersonalityRules {
  attacksPlayers: boolean;
  /** Only attack targets whose HQ is at most this many levels above the bot's own. */
  maxHqAbove: number;
  /** Never attack targets more than this many levels below (mirrors the no-loot rule). */
  maxHqBelow: number;
  /** Buys a shield after this many attacks received within an hour. */
  shieldAfterAttacks: number;
  gatherFirst: boolean;
  packLevelOffset: number;
  revenge: boolean;
}

export const PERSONALITY_RULES: Record<BotPersonality, PersonalityRules> = {
  turtle: { attacksPlayers: false, maxHqAbove: 0, maxHqBelow: 0, shieldAfterAttacks: 1, gatherFirst: true, packLevelOffset: -2, revenge: false },
  farmer: { attacksPlayers: false, maxHqAbove: 0, maxHqBelow: 0, shieldAfterAttacks: 2, gatherFirst: true, packLevelOffset: -1, revenge: false },
  raider: { attacksPlayers: true, maxHqAbove: 0, maxHqBelow: 6, shieldAfterAttacks: 3, gatherFirst: false, packLevelOffset: 0, revenge: true },
  opportunist: { attacksPlayers: true, maxHqAbove: -2, maxHqBelow: 6, shieldAfterAttacks: 2, gatherFirst: true, packLevelOffset: 0, revenge: true },
  warlord: { attacksPlayers: true, maxHqAbove: 2, maxHqBelow: 6, shieldAfterAttacks: 4, gatherFirst: false, packLevelOffset: 1, revenge: true },
  newbie: { attacksPlayers: false, maxHqAbove: 0, maxHqBelow: 0, shieldAfterAttacks: 1, gatherFirst: true, packLevelOffset: -3, revenge: false },
};

export const BOT_TURN_MIN_MINUTES = 5;
export const BOT_TURN_MAX_MINUTES = 15;
export const BOT_ATTACK_COOLDOWN_HOURS = 12;
